import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAlert } from '../../components/ConfirmModal';
import { Link, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';

const PLAN_COLOR  = { free: '#6b7280', basic: '#6f42c1', family: '#059669' };
const PLAN_BG     = { free: 'var(--header-bg)', basic: 'var(--purple-section)', family: 'var(--green-section)' };
const STATUS_COLOR = { active: 'success', inactive: 'secondary', cancelled: 'danger', trialing: 'info' };

const AdminUsers = () => {
  const alert = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [pages, setPages]     = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const search    = searchParams.get('search')    || '';
  const plan      = searchParams.get('plan')      || '';
  const status    = searchParams.get('status')    || '';
  const sort      = searchParams.get('sort')      || 'newest';
  const page      = parseInt(searchParams.get('page') || '1');

  const [searchInput, setSearchInput] = useState(search);

  // Renamed from `fetch` — it shadowed the global, which is a trap for anyone
  // later reaching for the real one in this file.
  //
  // requestId: five filter deps rebuild this callback, so the effect refires on
  // every search keystroke, plan/status/sort change and page turn. Without a
  // guard an earlier response can land last and repaint the table while the
  // filters show something else — and on this screen the rows carry suspend and
  // delete actions, so acting on a stale row means acting on the wrong account.
  const requestIdRef = useRef(0);
  const loadUsers = useCallback(() => {
    const requestId = ++requestIdRef.current;
    const isStale = () => requestId !== requestIdRef.current;
    setLoading(true);
    const params = { page, limit: 20, sort };
    if (search)  params.search = search;
    if (plan)    params.plan   = plan;
    if (status)  params.status = status;
    API.get('/admin/users', { params })
      .then(r => {
        if (isStale()) return;
        setUsers(r.data.users); setTotal(r.data.total); setPages(r.data.pages);
      })
      .catch(() => { if (!isStale()) setError('Failed to load users.'); })
      .finally(() => { if (!isStale()) setLoading(false); });
  }, [search, plan, status, sort, page]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const set = (key, val) => {
    const p = Object.fromEntries(searchParams);
    if (val) p[key] = val; else delete p[key];
    if (key !== 'page') p.page = '1';
    setSearchParams(p);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    set('search', searchInput);
  };

  return (
    <AdminLayout title="Users">
      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
        <div className="card-body p-3">
          <div className="row g-2 align-items-center">
            <div className="col-md-4">
              <form onSubmit={handleSearch} className="d-flex gap-2">
                <input
                  className="form-control form-control-sm"
                  placeholder="Search name or email…"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  style={{ borderRadius: '8px' }}
                />
                <button type="submit" className="btn btn-sm btn-dream rounded-pill px-3">
                  <i className="bi bi-search"></i>
                </button>
              </form>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" style={{ borderRadius: '8px' }}
                value={plan} onChange={e => set('plan', e.target.value)}>
                <option value="">All Plans</option>
                <option value="free">Free</option>
                <option value="basic">Basic</option>
                <option value="family">Family</option>
              </select>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" style={{ borderRadius: '8px' }}
                value={status} onChange={e => set('status', e.target.value)}>
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="cancelled">Cancelled</option>
                <option value="trialing">Trialing</option>
              </select>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" style={{ borderRadius: '8px' }}
                value={sort} onChange={e => set('sort', e.target.value)}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="name">By name</option>
                <option value="stories">Most active</option>
              </select>
            </div>
            <div className="col-auto ms-auto d-flex align-items-center gap-3">
              <span className="text-muted small">{total.toLocaleString()} users</span>
              <button
                className="btn btn-sm btn-outline-secondary rounded-pill"
                style={{ fontSize: '12px' }}
                onClick={async () => {
                  try {
                    const r = await API.get('/admin/export/users', { responseType: 'blob' });
                    const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (e) { await alert({ title: 'Export failed', message: 'Could not export users CSV. Please try again.', danger: true }); }
                }}>
                <i className="bi bi-download me-1"></i>Export CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
        </div>
      ) : (
        <>
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ background: 'var(--header-bg)' }}>
                  <tr>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>USER</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>PLAN</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>STATUS</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>CHILDREN</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>STORIES/MO</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>JOINED</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr><td colSpan={7} className="text-center text-muted py-5">No users found</td></tr>
                  ) : users.map(u => (
                    <tr key={u._id} style={{ verticalAlign: 'middle' }}>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                        <div className="d-flex align-items-center gap-2">
                          <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white flex-shrink-0"
                            style={{ width: '34px', height: '34px', background: '#6f42c1', fontSize: '13px' }}>
                            {u.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-main)' }}>
                              {u.name}
                              {u.isAdmin && <span className="badge bg-danger ms-1" style={{ fontSize: '10px' }}>Admin</span>}
                              {u.isSuspended && <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '10px' }}>Suspended</span>}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                        <span className="badge rounded-pill px-2 py-1" style={{ background: PLAN_BG[u.subscription?.plan], color: PLAN_COLOR[u.subscription?.plan], fontSize: '12px' }}>
                          {u.subscription?.plan || 'free'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                        <span className={`badge bg-${STATUS_COLOR[u.subscription?.status] || 'secondary'} bg-opacity-10 text-${STATUS_COLOR[u.subscription?.status] || 'secondary'}`} style={{ fontSize: '12px' }}>
                          {u.subscription?.status || 'inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '14px', color: 'var(--text-body)' }}>
                        {u.childCount || 0}
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '14px', color: 'var(--text-body)' }}>
                        {u.storiesGeneratedThisMonth || 0}
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '13px', color: 'var(--text-muted2)' }}>
                        {new Date(u.createdAt).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                        <Link to={`/admin/users/${u._id}`} className="btn btn-sm btn-outline-secondary rounded-pill px-3" style={{ fontSize: '12px' }}>
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="d-flex justify-content-center gap-2 mt-4">
              <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page <= 1}
                onClick={() => set('page', String(page - 1))}>← Prev</button>
              <span className="btn btn-sm disabled" style={{ background: 'var(--header-bg)', border: 'none', color: '#6f42c1' }}>
                {page} / {pages}
              </span>
              <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page >= pages}
                onClick={() => set('page', String(page + 1))}>Next →</button>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default AdminUsers;
